import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import BackToTop from "@/components/base/BackToTop";
import PopularCategoriesStrip from "@/pages/categories/az/components/PopularCategoriesStrip";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { azCategories } from "@/mocks/azCategories";
import type { AzCategory } from "@/mocks/azCategories";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function CategoryAtoZ() {
  const [params] = useSearchParams();
  const { settings } = useSiteSettings();
  const [query, setQuery] = useState("");
  const didAutoScroll = useRef(false);

  const letterParam = (params.get("letter") || "").toUpperCase();
  const validLetter = ALPHABET.includes(letterParam) ? letterParam : "";

  const total = azCategories.length;

  const letterGroups = useMemo(
    () => new Set(azCategories.map((c) => c.name.charAt(0).toUpperCase())).size,
    [],
  );

  const presentLetters = useMemo(
    () => new Set(azCategories.map((c) => c.name.charAt(0).toUpperCase())),
    [],
  );

  const trimmedQuery = query.trim().toLowerCase();

  const filtered = useMemo(
    () =>
      trimmedQuery
        ? azCategories.filter((c) => c.name.toLowerCase().includes(trimmedQuery))
        : azCategories,
    [trimmedQuery],
  );

  const groups = useMemo(() => {
    const map = new Map<string, AzCategory[]>();
    filtered.forEach((c) => {
      const letter = c.name.charAt(0).toUpperCase();
      const bucket = map.get(letter);
      if (bucket) bucket.push(c);
      else map.set(letter, [c]);
    });
    return ALPHABET.filter((l) => map.has(l)).map((l) => ({
      letter: l,
      items: (map.get(l) as AzCategory[]).slice().sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, [filtered]);

  useEffect(() => {
    if (!validLetter || didAutoScroll.current) return;
    didAutoScroll.current = true;
    const raf = requestAnimationFrame(() => {
      document.getElementById(`letter-${validLetter}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(raf);
  }, [validLetter]);

  const jumpTo = (letter: string) => {
    document.getElementById(`letter-${letter}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <Seo
        title="A to Z Business Categories"
        description="Browse every business category in alphabetical order — from AC dealers and bakeries to schools, tailors, hospitals and hotels across Mysore, Mandya, Chamarajanagar and Kodagu."
        keywords="A to Z business categories, category index Mysore, business types Mandya, local directory categories, alphabetical business index"
        path="/categories-a-z"
      />

      {/* Hero / intro band */}
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-12">
          <Breadcrumbs items={[{ label: "Categories", to: "/categories" }, { label: "A to Z Categories" }]} />

          <div className="mt-3 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-4xl">
                A to Z business categories
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                Every business type on our directory, arranged alphabetically. Pick a letter to jump straight to that
                section, then tap any category to see matching listings across Mysore, Mandya, Chamarajanagar and
                Kodagu.
              </p>
            </div>
            <div className="flex shrink-0 gap-3">
              <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-3">
                <p className="font-heading text-xl font-extrabold text-primary-700">{letterGroups}</p>
                <p className="mt-0.5 text-xs font-medium text-foreground-500">Letter groups</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-3">
                <p className="font-heading text-xl font-extrabold text-accent-600">{total}+</p>
                <p className="mt-0.5 text-xs font-medium text-foreground-500">Business categories</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular categories */}
      {settings.showPopularCategories && <PopularCategoriesStrip />}

      {/* Sticky search + jump bar */}
      <div className="sticky top-[64px] z-40 border-y border-background-200 bg-background-50/95 backdrop-blur md:top-[100px]">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-3 md:px-6">
          <div className="relative">
            <span className="pointer-events-none absolute left-3.5 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search categories by name — try “salon”, “ac repair”, “legal”, “fishing”…"
              aria-label="Search business categories by name"
              className="w-full rounded-md border border-background-200 bg-background-50 py-2.5 pl-10 pr-24 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
            {trimmedQuery && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 inline-flex -translate-y-1/2 items-center gap-1.5 rounded-md bg-background-100 px-2.5 py-1.5 text-xs font-semibold text-foreground-600 transition-colors hover:bg-background-200 cursor-pointer"
              >
                <i className="ri-close-line text-sm"></i>
                Clear
              </button>
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <span className="mr-1 hidden text-xs font-semibold uppercase tracking-wide text-foreground-500 sm:inline">
              Jump to
            </span>
            {ALPHABET.map((letter) => {
              const enabled = presentLetters.has(letter);
              const isCurrent = validLetter === letter;
              return (
                <button
                  key={letter}
                  type="button"
                  disabled={!enabled}
                  onClick={() => jumpTo(letter)}
                  aria-label={`Go to categories starting with ${letter}`}
                  className={`flex h-8 min-w-8 items-center justify-center rounded-md px-2 text-xs font-bold uppercase transition-colors duration-200 ${
                    isCurrent
                      ? "bg-primary-500 text-foreground-950"
                      : enabled
                        ? "bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900 cursor-pointer"
                        : "bg-background-100/60 text-foreground-300 cursor-not-allowed"
                  }`}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Grouped index */}
      <section className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
        {trimmedQuery && (
          <p className="mb-6 text-sm text-foreground-600">
            <strong className="font-semibold text-foreground-950">{filtered.length}</strong>{" "}
            {filtered.length === 1 ? "category" : "categories"} matching “{query.trim()}”
          </p>
        )}

        {groups.length === 0 ? (
          <div className="rounded-lg border border-background-200 bg-background-50 px-6 py-14 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-line text-2xl"></i>
            </span>
            <p className="mt-4 font-heading text-base font-bold text-foreground-950">No matching categories</p>
            <p className="mt-1 text-sm text-foreground-500">
              Try a different keyword, or browse the full list below.
            </p>
            <button
              type="button"
              onClick={() => setQuery("")}
              className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              Reset search
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-12">
            {groups.map(({ letter, items }) => (
              <section key={letter} id={`letter-${letter}`} className="scroll-mt-[210px] md:scroll-mt-[260px]">
                <div className="flex flex-wrap items-center gap-3 border-b border-background-200 pb-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-500 font-heading text-lg font-extrabold text-foreground-950">
                    {letter}
                  </span>
                  <h2 className="font-heading text-base font-bold text-foreground-950 md:text-lg">
                    Starting with {letter}
                  </h2>
                  <span className="ml-auto inline-flex items-center whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
                    {items.length} {items.length === 1 ? "category" : "categories"}
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-0.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {items.map((c) => (
                    <Link
                      key={c.name}
                      to={`/directory?q=${encodeURIComponent(c.name)}`}
                      title={`Search listings for ${c.name}`}
                      className="group flex items-center justify-between gap-3 rounded-md px-3 py-2 transition-colors duration-200 hover:bg-secondary-50 cursor-pointer"
                    >
                      <span className="flex min-w-0 items-center gap-2 text-sm text-foreground-700 group-hover:text-primary-700">
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center text-foreground-400 group-hover:text-accent-600">
                          <i className="ri-folder-3-line text-sm"></i>
                        </span>
                        <span className="truncate">{c.name}</span>
                      </span>
                      <span className="flex shrink-0 items-center gap-1.5">
                        <span className="rounded-full bg-background-100 px-2 py-0.5 text-[11px] font-semibold text-foreground-500 group-hover:bg-accent-100 group-hover:text-accent-900">
                          {c.count}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </section>

      <BackToTop showAfter={520} />
    </>
  );
}